import { Async, Dispatchers, TypedOmit } from '@client/new/helpers/types';
import type {
  AuthModelActionsMap,
  FlowSessionSource,
  RegistrationDraft,
} from '../model/auth.types';
import {
  ProfileFlowStep,
  RegisterStep,
  registerStepConfig,
} from '../model/registration.model';

export const authVmCopies = {
  common: {
    continue: 'Continue',
    creatorTestimonialsAriaLabel: 'Creator testimonials',
    emailAddressPlaceholder: 'Email address',
  },
} as const;

export type RegisterValues = RegistrationDraft & {
  code: string;
};

type RegisterStepField<TStep extends RegisterStep> =
  (typeof registerStepConfig)[TStep]['fields'][number];
type RegisterSessionField = RegisterStepField<RegisterStep>;

export type RegisterStepValuesMap = {
  [TStep in RegisterStep]: Pick<RegisterValues, RegisterStepField<TStep>>;
};

export type RegisterStepValuesWithoutCode = TypedOmit<RegisterValues, 'code'>;
export type RegisterSessionValues = Partial<
  Pick<RegisterValues, RegisterSessionField>
>;

export type RegisterEmailStepValues = RegisterStepValuesMap['email'];
export type RegisterProfileStep = ProfileFlowStep;

export type RegisterSetFieldActionPayload<
  TField extends keyof RegisterValues = keyof RegisterValues,
> = {
  step: RegisterStep;
  field: TField;
  value: RegisterValues[TField];
};

export type SsoProviderState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'error'; message: string };

export type RegisterActionMap = {
  setField: RegisterSetFieldActionPayload;
  continue: null;
  back: null;
  restartFlow: null;
  skipPhone: null;
  submitCode: null;
  resendCode: null;
  googleSso: null;
  appleSso: null;
};

export type RegisterSubmissionStage =
  | 'sending-challenge'
  | 'verifying-code'
  | 'registering-account';

type RegistrationChallengePayload =
  AuthModelActionsMap['SendRegistrationChallenge'];
type RegistrationChallengeMode = NonNullable<
  NonNullable<RegistrationChallengePayload['options']>['mode']
>;

export type RegisterSubmissionPayloadByStage = {
  'sending-challenge': Pick<RegistrationChallengePayload, 'email'> & {
    source: FlowSessionSource;
    mode: RegistrationChallengeMode;
  };
  'verifying-code': Pick<
    AuthModelActionsMap['SubmitRegistrationCode'],
    'email' | 'code'
  > & {
    source: FlowSessionSource;
  };
  'registering-account': AuthModelActionsMap['CompleteRegistration'];
};

type RegisterPendingSubmissionState<TStage extends RegisterSubmissionStage> = {
  type: TStage;
  payload: RegisterSubmissionPayloadByStage[TStage];
};

type RegisterErrorSubmissionState<TStage extends RegisterSubmissionStage> = {
  type: 'error';
  message: string;
  stage: TStage;
  payload: RegisterSubmissionPayloadByStage[TStage];
};

export type RegisterSubmissionState =
  | { type: 'idle' }
  | RegisterPendingSubmissionState<'sending-challenge'>
  | RegisterPendingSubmissionState<'verifying-code'>
  | RegisterPendingSubmissionState<'registering-account'>
  | RegisterErrorSubmissionState<'sending-challenge'>
  | RegisterErrorSubmissionState<'verifying-code'>
  | RegisterErrorSubmissionState<'registering-account'>;

export type RegisterState = {
  step: RegisterStep;
  values: RegisterValues;
  submission: RegisterSubmissionState;
  canContinue: boolean;
  googleSsoState: SsoProviderState;
  appleSsoState: SsoProviderState;
};

export type RegisterViewModel = {
  state: RegisterState;
  toStepFormState: <TStep extends RegisterStep>(
    step: TStep,
  ) => RegisterStepFormState<TStep>;
} & Dispatchers<RegisterActionMap>;

export type RegisterDispatch = RegisterViewModel['dispatch'];
export type RegisterStepFormState<TStep extends RegisterStep> = Async<
  RegisterStepValuesMap[TStep]
>;

export type RegisterStepSharedProps = {
  canContinue: boolean;
  shouldAutoFocusInput: boolean;
  dispatch: RegisterDispatch;
};

export type { RegisterStep } from '../model/registration.model';
